import getPostTypeStaticPaths from '@/functions/wp/posts/getPostTypeStaticPaths';
import getPostTypeStaticProps from '@/functions/wp/posts/getPostTypeStaticProps';
import Blocks from '@/components/Blocks';
import Container from '@/components/Container';
import Layout from '@/components/Layout';
import BlockHeading from '@/components/BlockHeading';
import BlockFeatureImage from '@/components/BlockFeatureImage';
import SocialShare from '@/components/SocialShare';
import EventStatus from '@/components/EventStatus';
import styles from './Event.module.scss';
import Moment from 'react-moment';

const postType = 'event';

/**
 * Render the Post component
 *
 * @param  {object}  props      component attributes as props
 * @param  {object}  props.post post data from WP
 * @return {Element}            Post component
 */
// eslint-disable-next-line react/prop-types
export default function Post({ post }) {
  return (
    <Layout seo={{ ...post?.seo }} hasJsonLd hasHubSpot>
      <div className={styles.events_page}>
        <div className={styles.events_wrapper}>
          <Container condensed nomargin>
            <div className={styles.article__featured_image}>
              <BlockFeatureImage
                url={post?.featuredImage?.node?.sourceUrl ?? ''}
                alt={post?.featuredImage?.node?.altText}
                caption={post?.featuredImage?.node?.caption}
              />
            </div>
          </Container>
          <div className={styles.event_details} />
        </div>
        <div className={styles.events_wrapper}>
          <Container condensed nomargin>
            <div className={styles.event_meta}>
              <div>
                <BlockHeading level={1} content={post?.title} />
                <div>
                  <Moment format="MMMM D">{post?.events?.startDate}</Moment>
                  {post?.events?.endDate && (
                    <>
                      {' - '}<Moment format="D">{post?.events?.endDate}</Moment>
                    </>
                  )}
                </div>
              </div>
              <EventStatus status={post?.events?.status} />
            </div>
            <hr/>
            <Blocks blocks={post?.blocks} />
            <div>
              {process.browser && (
                <div className={styles.article__socialShare}>
                  <p>SHARE EVENT:</p>
                  <SocialShare title={post?.title} url={window?.location?.href} />
                </div>
              )}
            </div>
          </Container>
          <div className={styles.event_details}>
            <div className={styles.event_details_title}>
              <h3>Details</h3>
            </div>
            <div className={styles.event_details_group}>
              <h4>Date & Time</h4>
              <div>
                <Moment format="MMMM D YYYY">{post?.events?.startDate}</Moment>
                {post?.events?.startTime && (
                  <span>{post?.events?.startTime}</span>
                )}
              </div>
              {post?.events?.endDate && (
                <div>
                  <Moment format="MMMM D YYYY">{post?.events?.endDate}</Moment>
                  {post?.events?.endTime && (
                    <span>{post?.events?.endTime}</span>
                  )}
                </div>
              )}
            </div>
            {post?.events?.location && (
              <div className={styles.event_details_group}>
                <h4>Location</h4>
                <span>{post?.events?.location}</span>
              </div>
            )}
            {post?.events?.organizerName && (
              <div className={styles.event_details_group}>
                <h4>Organizer</h4>
                <span>{post?.events?.organizerName}</span>
              </div>
            )}
            {post?.events?.organizerUrl && (
              <div className={styles.event_details_group}>
                <h4>External Links</h4>
                <a href={post?.events?.organizerUrl} target="_blank">{post?.events?.organizerName}</a>
              </div>
            )}
            <hr />
          </div>
        </div>
      </div>
    </Layout>
  );
}

/**
 * Get post static paths.
 *
 * @return {object} Array of paths and fallbacks
 */
export async function getStaticPaths() {
  return getPostTypeStaticPaths(postType);
}

/**
 * Get post static props
 *
 * @param  {object} context        context for current post
 * @param  {object} context.params route parameters for current post
 * @return {object}                post props
 */
export async function getStaticProps({ params }) {
  return getPostTypeStaticProps(params, postType);
}
